import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  limit,
} from 'firebase/firestore';
import { app } from '../firebase';
import { Calendar, User, Clock, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const db = getFirestore(app);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const q = query(
          collection(db, 'blog_posts'),
          where('slug', '==', slug),
          where('status', '==', 'published'),
          limit(1)
        );
        const snap = await getDocs(q);
        if (snap.empty) {
          setNotFound(true);
        } else {
          const d = snap.docs[0];
          setPost({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate(),
            publishedAt: d.data().publishedAt?.toDate(),
          });
        }

        const allQ = query(
          collection(db, 'blog_posts'),
          where('status', '==', 'published'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const allSnap = await getDocs(allQ);
        setRelatedPosts(
          allSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((p) => p.slug !== slug)
        );
      } catch (err) {
        console.error('Error fetching post:', err);
        setNotFound(true);
      }
      setLoading(false);
    };
    fetchPost();
  }, [db, slug]);

  const formatDate = (p) => {
    const d = p?.publishedAt || p?.createdAt;
    if (!d) return '';
    return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isoDate = (p) => {
    const d = p?.publishedAt || p?.createdAt;
    return d ? d.toISOString().split('T')[0] : '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-display font-bold text-white mb-4">Post Not Found</h1>
          <p className="text-slate-400 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:opacity-90 transition-opacity"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        article={{
          datePublished: isoDate(post),
          author: post.author,
        }}
      />

      <article>
        {/* Hero */}
        <header className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/15 rounded-full blur-[128px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/15 rounded-full blur-[128px] pointer-events-none" />

          <div className="relative max-w-3xl mx-auto px-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors text-sm font-mono mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-mono font-semibold uppercase tracking-wider">
              {post.category}
            </span>

            <h1 className="font-display text-4xl sm:text-5xl font-bold text-white leading-tight mt-4">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-slate-400">
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <time dateTime={isoDate(post)}>{formatDate(post)}</time>
              </span>
              {post.readTime && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-6 pb-24">
          <div className="prose prose-invert prose-lg max-w-none prose-headings:font-display prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-slate-300 prose-p:leading-relaxed prose-strong:text-white prose-a:text-cyan-400 hover:prose-a:text-cyan-300 prose-li:text-slate-300 prose-ul:my-4 prose-ol:my-4 prose-blockquote:border-cyan-500/40 prose-blockquote:text-slate-400">
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </div>

          {/* Navigation */}
          {relatedPosts.length > 0 && (
            <nav className="mt-16 pt-8 border-t border-white/[0.06]">
              <h3 className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-4">
                More Posts
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedPosts.slice(0, 2).map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 hover:bg-white/[0.06] transition-colors"
                  >
                    <span className="text-xs font-mono text-cyan-400/60 uppercase tracking-wider">
                      {p.category}
                    </span>
                    <p className="text-white font-display font-semibold mt-1 group-hover:text-cyan-300 transition-colors">
                      {p.title}
                    </p>
                  </Link>
                ))}
              </div>
            </nav>
          )}

          {/* CTA */}
          <div className="mt-16 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-indigo-500/10 border border-white/[0.06] p-10 text-center">
            <h3 className="font-display text-2xl font-bold text-white">
              Want to discuss this further?
            </h3>
            <p className="mt-3 text-slate-400 max-w-md mx-auto">
              Get in touch to explore how these insights apply to your projects.
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogPost;
